/**
 * Canonical Courses
 * ----------------------------------------------------------------------------
 * Single source of truth for Course schema (JSON-LD) across the site.
 *
 * Every Course JSON-LD block - homepage OfferCatalog, /programs/* page-level
 * Course schema, and any future Course markup - must be derived from this
 * array so the same canonical entity (`@id`) is referenced everywhere.
 *
 * The visible UI labels in headers, footers and carousels should match
 * `name` here. If the marketing label changes, change it here once.
 *
 * Do NOT add visible UI strings, prices or feature bullets here - this file
 * is structured-data only.
 */

export const SITE_URL = 'https://schoolola.com.au' as const;

export const CANONICAL_PROVIDER = {
  '@type': 'EducationalOrganization' as const,
  name: 'Schoolola',
  url: `${SITE_URL}/`,
};

export interface CanonicalCourse {
  /** Stable slug used to build the @id and url */
  slug: string;
  /** Path on the site (used for @id + url) */
  path: string;
  /** Canonical course name - must match the visible UI label */
  name: string;
  /** Plain-text course description */
  description: string;
  /** Schema.org educationalLevel */
  educationalLevel: string;
  /** ISO 8601 duration, e.g. "P12M". Optional. */
  timeRequired?: string;
  /** Course delivery modes */
  courseMode: Array<'In-person' | 'Online'>;
}

export type CourseSchemaOptions = {
  workload?: string;
  schedule?: { repeatFrequency: string; duration: string };
  offers?: Record<string, unknown>;
  provider?: Record<string, unknown>;
};

export const canonicalCourses: CanonicalCourse[] = [
  {
    slug: 'foundations',
    path: '/programs/foundations',
    name: 'Foundations Course',
    description:
      'Build strong mathematical and literacy foundations for primary school students from Kindergarten, Prep or Pre-primary to Year 2.',
    educationalLevel: 'Primary (Kindergarten, Prep or Pre-primary to Year 2)',
    courseMode: ['In-person', 'Online'],
  },
  {
    slug: 'oc-tmrw',
    path: '/programs/oc-preparation',
    name: 'OC TMRW™ Course',
    description:
      'Comprehensive Opportunity Class exam preparation for Year 3 and Year 4 students using the TMRW™ methodology.',
    educationalLevel: 'Primary (Years 3-4)',
    timeRequired: 'P12M',
    courseMode: ['In-person', 'Online'],
  },
  {
    slug: 'selective-tmrw',
    path: '/programs/selective-school',
    name: 'Selective TMRW™ Course',
    description:
      'Expert coaching for the NSW Selective High School Placement Test, delivered using the TMRW™ methodology.',
    educationalLevel: 'Primary (Years 5-6)',
    timeRequired: 'P18M',
    courseMode: ['In-person', 'Online'],
  },
  {
    slug: 'vsec',
    path: '/programs/vsec',
    name: 'Victorian Selective Entry Course (VSEC)',
    description:
      'Victoria Selective Entry High School preparation for Year 7 and Year 8 students.',
    educationalLevel: 'Secondary (Years 7-8)',
    courseMode: ['In-person', 'Online'],
  },
  {
    slug: 'naplan',
    path: '/programs/naplan-preparation',
    name: 'NAPLAN Preparation Course',
    description:
      'NAPLAN preparation for students sitting Years 3, 5 and 7 NAPLAN covering Reading, Writing, Numeracy and Conventions of Language.',
    educationalLevel: 'Years 3, 5 and 7',
    timeRequired: 'P3M',
    courseMode: ['In-person', 'Online'],
  },
  {
    slug: 'tmrw-leap',
    path: '/programs/tmrw-leap',
    name: 'OC to Selective Bridging Course',
    description:
      'Advanced bridging programme for Year 4 students who have completed the OC exam, preparing them for Selective High School coaching.',
    educationalLevel: 'Primary (Year 4)',
    courseMode: ['In-person', 'Online'],
  },
];

/** Build the `@id` for a canonical course. */
export const courseId = (slug: string): string => {
  const c = canonicalCourses.find((x) => x.slug === slug);
  if (!c) throw new Error(`Unknown canonical course slug: ${slug}`);
  return `${SITE_URL}${c.path}#course`;
};

/**
 * Build a full Course JSON-LD object for a single course.
 * Use on /programs/* pages.
 */
export const buildCourseSchema = (slug: string, opts: CourseSchemaOptions = {}) => {
  const c = canonicalCourses.find((x) => x.slug === slug);
  if (!c) throw new Error(`Unknown canonical course slug: ${slug}`);
  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    '@id': `${SITE_URL}${c.path}#course`,
    name: c.name,
    description: c.description,
    url: `${SITE_URL}${c.path}`,
    provider: opts.provider ?? CANONICAL_PROVIDER,
    educationalLevel: c.educationalLevel,
    ...(c.timeRequired ? { timeRequired: c.timeRequired } : {}),
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: c.courseMode.length > 1 ? 'blended' : c.courseMode[0],
      ...(opts.workload ? { courseWorkload: opts.workload } : {}),
      ...(opts.schedule
        ? { courseSchedule: { '@type': 'Schedule', ...opts.schedule } }
        : {}),
    },
    ...(opts.offers ? { offers: opts.offers } : {}),
  };
};

/** Organisation used as provider on course page Course schema. */
export const TUTORING_PROVIDER = {
  '@type': 'Organization' as const,
  name: 'Schoolola Tutoring',
  url: `${SITE_URL}/`,
};

/** Offer from a course_pricing row (or AggregateOffer across several). Returns undefined when no row. */
export const offersFromPricing = (rows: { price_per_session: number | string }[] | undefined) => {
  if (!rows || rows.length === 0) return undefined;
  const prices = rows.map((r) => Number(r.price_per_session));
  const lo = Math.min(...prices);
  const hi = Math.max(...prices);
  if (lo === hi) {
    return { '@type': 'Offer', price: lo.toFixed(2), priceCurrency: 'AUD', priceSpecification: 'per session' };
  }
  return { '@type': 'AggregateOffer', lowPrice: lo.toFixed(2), highPrice: hi.toFixed(2), priceCurrency: 'AUD' };
};

/**
 * Build an OfferCatalog itemListElement array for the homepage.
 * Each Offer's `itemOffered` references the canonical Course by `@id`
 * rather than duplicating the full Course object - Google then sees one
 * canonical Course entity per programme.
 */
/**
 * Build an OfferCatalog itemListElement array for the homepage.
 *
 * Each Offer's `itemOffered` is a THIN reference to the canonical Course
 * by `@id` + `url` only - the full Course object is emitted exactly once,
 * on its own canonical /programs/* page (via buildCourseSchema). This
 * prevents Rich Results from reporting the same course twice.
 */
export const buildHomepageOfferCatalog = () => ({
  '@type': 'OfferCatalog',
  name: 'Tutoring Courses',
  itemListElement: canonicalCourses.map((c) => ({
    '@type': 'Offer',
    itemOffered: {
      '@type': 'Course',
      '@id': `${SITE_URL}${c.path}#course`,
      url: `${SITE_URL}${c.path}`,
      name: c.name,
    },
  })),
});
